import React, { useMemo } from 'react';
import { Box, BoxProps } from 'components/ui';
import styled from 'styled-components/macro';
import RecordImg from 'img/record.svg';
import { DROP, Original } from '../common';
import { toHexSeed } from 'utils/token';
import Visualization from 'components/Visualization';
import EnigmaVisualization from 'components/EnigmaVisualization';
import { getDropFromContractAddress } from 'utils/dropsHelpers';
import { useWindowSize } from 'hooks/useWindowSize';
import FuturaVisualization from 'components/FuturaVisualization';
import { getMixCodeFromAttributes } from 'utils/futura';
import futuraSpec from 'utils/futuraSpec.json';
import { FUTURA_ATTRIBUTES } from '../../constants';
import { SCREEN_SIZES } from 'utils/styles';

interface PlayerVizProps {
  vizSize?: number;
  activeBeat: Original | null;
  onPlay?: any;
  onInit?(any): void;
  fixedSize?: boolean;
  playbackRate?: number;
  isFullScreen?: boolean;
}

const getVizSize = ws => {
  if (ws.width < SCREEN_SIZES.sm) {
    return 260;
  }
  if (ws.width < SCREEN_SIZES.lg && ws.height > SCREEN_SIZES.md) {
    return 400;
  }
  if (ws.height > SCREEN_SIZES.md) {
    return 624;
  }
  return ws.height - 440;
};

const RecordBg = styled(Box)`
  pointer-events: none;
`;

const VisWrapper = styled.div<any>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  margin: 0 auto;
  transform: scale(${props => props.size / 480});
  transform-origin: 50% 50%;

  & > div {
    position: relative;
    top: ${props => (props.size - 480) / 2}px;
    left: ${props => (props.size - 480) / 2}px;
  }

  /* override animation speeds */
  ${props =>
    props.playbackRate &&
    `
    .eulerbeats-main.eulerbeats-is-playing {
      animation-duration: ${6 / props.playbackRate}s !important;
    }
    .eulerbeats-main.eulerbeats-is-playing .spin-clock {
      animation-duration: ${10 / props.playbackRate}s !important;
    }
  
    .eulerbeats-main.eulerbeats-is-playing .spin-counter {
      animation-duration: ${10 / props.playbackRate}s !important;
    }
  `}
`;

const FuturaViz = ({ item, isFullScreen }) => {
  const EBF = window.EulerBeatsFutura;
  const tempo = item?.attributes[FUTURA_ATTRIBUTES.bpm];
  const pieceSpec = useMemo(() => EBF.utils.updatePieceSpecRaw(futuraSpec), [EBF]);
  const pieceData = useMemo(
    () =>
      EBF.utils.buildPieceData({
        pieceSpec,
        mixCode: getMixCodeFromAttributes(item?.attributes),
        tempo,
      }),
    [item, EBF.utils, pieceSpec, tempo]
  );

  return (
    <Box ml={isFullScreen ? '-500px' : '0'}>
      <FuturaVisualization
        key={item.token_id || item.id}
        elementId={item.token_id || item.id}
        width={isFullScreen ? 400 : 456}
        height={isFullScreen ? 400 : 456}
        twists={item.attributes[FUTURA_ATTRIBUTES.twists]}
        distortion={item.attributes[FUTURA_ATTRIBUTES.distortion]}
        noteShape={item.attributes[FUTURA_ATTRIBUTES.noteShape]}
        pieceData={pieceData}
        paletteName={item.attributes[FUTURA_ATTRIBUTES.colorPalette]}
      />
    </Box>
  );
};

const PlayerViz: React.FC<PlayerVizProps & BoxProps> = ({
  activeBeat,
  vizSize = 280,
  fixedSize,
  onPlay = () => {},
  playbackRate,
  onInit,
  isFullScreen,
}) => {
  const drop = activeBeat && getDropFromContractAddress(activeBeat.contractAddress);
  const isFutura = drop === DROP.futura;
  const ws = useWindowSize();

  if (!fixedSize) {
    vizSize = getVizSize(ws);
  }
  if (isFullScreen) {
    vizSize = ws.height;
    if (!isFutura) {
      vizSize -= 80;
    }
  }

  if (!vizSize) {
    return null;
  }

  const seed = drop && activeBeat && toHexSeed(drop, activeBeat.id);

  return (
    <Box width={vizSize} margin="0 auto" py={[2, 4]}>
      <RecordBg
        backgroundImage={[`url(${RecordImg})`, `url(${RecordImg})`, 'none']}
        backgroundRepeat="no-repeat"
        backgroundPosition="center"
        position="absolute"
        top={['-82px', '-58px']}
        left={['0']}
        height={['456px']}
        width={['100%']}
      />
      <VisWrapper size={vizSize} playbackRate={playbackRate}>
        {seed && drop === DROP.genesis && <Visualization key={seed} seed={seed} onPlay={onPlay} />}
        {seed && drop === DROP.enigma && (
          <EnigmaVisualization key={seed} seed={seed} onPlay={onPlay} />
        )}
        {seed && drop === DROP.futura && (
          <FuturaViz item={activeBeat} isFullScreen={isFullScreen} />
        )}
      </VisWrapper>
    </Box>
  );
};

export default PlayerViz;
